// ═══════════════════════════════════════════════════════════════════════════
// PAGE: NOTIFICATIONS (mai 2026)
// Migration S5.3 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════
//
// Liste les notifications visibles par l'user (DC/DM/DD/DS sur les projets
// liés à l'action). Filtres : Programme + Projet + Niveau (init NNA) +
// Lu/Non lu. Le libellé est reconstitué côté front depuis `event_type` +
// `change_reasons` + `new_status_label` pour respecter la migration i18n.

interface NotifEmitter {
  trigram?: string | null;
  name?: string;
}

interface Notification {
  id: number;
  action_id?: number | null;
  action_text?: string;
  action_alive?: boolean;
  event_type: 'action_created' | 'action_deleted' | 'action_updated' | string;
  change_reasons?: string[];
  new_status_label?: string;
  project_ids?: number[];
  emitter?: NotifEmitter | null;
  created_at?: string | null;
  is_read: boolean;
  // Notification ADRESSÉE (sollicitation d'arbitrage) : elle ne porte aucune
  // action, tout ce qu'elle a à dire est ici.
  review_id?: number | null;
  review_label?: string | null;
  count?: number | null;
  message?: string | null;
  entries?: string[];
}

interface FilterProject {
  id: number;
  code?: string | null;
  name: string;
}

interface FilterProgram {
  id: number;
  code?: string | null;
  name: string;
}

interface FilterStructure {
  id: number;
  code: string;
  label: string;
  is_active?: boolean;
}

interface NotifFilterOptions {
  programs?: FilterProgram[];
  projects?: FilterProject[];
}

interface NotifPrefs {
  notif_level_accepted?: number;
}

pages.notifications = async function () {
  setHeader(t('pageNotifications.title'), '');
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);

  const uid = await ensureCurrentUserId();
  if (!uid) {
    setContent(`<div class="error">${esc(t('common.errNotAuthenticated'))}</div>`);
    return;
  }

  let prefs: NotifPrefs;
  let filterOpts: NotifFilterOptions;
  let structures: FilterStructure[] = [];
  try {
    let allStructures: FilterStructure[];
    [prefs, filterOpts, allStructures] = (await Promise.all([
      api.getMyNotifPrefs(),
      api.getNotificationFilters(),
      (api.getStructuresIfAllowed() as Promise<FilterStructure[]>).catch(() => []),
    ])) as [NotifPrefs, NotifFilterOptions, FilterStructure[]];
    structures = (allStructures || [])
      .filter((s) => s.is_active !== false)
      .sort((a, b) => a.code.localeCompare(b.code));
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
    return;
  }

  const projectsById: Record<number, FilterProject> = Object.fromEntries(
    (filterOpts.projects || []).map((p) => [p.id, p]),
  );

  const state: {
    program_id: string;
    project_id: string;
    structure_id: string;
    max_nn: number | '';
    only_unread: '' | 'true' | 'false';
    notifications: Notification[];
  } = {
    program_id: '',
    project_id: '',
    structure_id: '',
    max_nn: typeof prefs.notif_level_accepted === 'number' ? prefs.notif_level_accepted : 2,
    only_unread: '',
    notifications: [],
  };

  async function loadNotifications(): Promise<void> {
    const qs = new URLSearchParams();
    if (state.program_id) qs.set('program_id', state.program_id);
    if (state.project_id) qs.set('project_id', state.project_id);
    if (state.structure_id) qs.set('structure_id', state.structure_id);
    if (state.max_nn !== '' && state.max_nn !== null) qs.set('max_nn', String(state.max_nn));
    if (state.only_unread === 'true') qs.set('only_unread', 'true');
    else if (state.only_unread === 'false') qs.set('only_unread', 'false');
    state.notifications = (await api.getNotifications(qs.toString())) as Notification[];
  }

  try {
    await loadNotifications();
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
    return;
  }

  function buildReasonsText(n: Notification): string {
    const reasons = Array.isArray(n.change_reasons) ? n.change_reasons : [];
    if (reasons.length === 0) return '';
    const parts: string[] = [];
    if (reasons.includes('deadline') || reasons.includes('deadline_revised')) {
      parts.push(t('pageNotifications.reason.deadline'));
    }
    if (reasons.includes('status_id')) {
      parts.push(t('pageNotifications.reason.status', { state: n.new_status_label || '—' }));
    }
    return parts.join(', ');
  }

  function buildLabel(n: Notification): string {
    const txt = n.action_text || '';
    // Une sollicitation d'arbitrage ne parle pas d'une action mais d'une revue.
    // Sans cette branche, elle retombe sur le libellé « mise à jour » avec un
    // titre vide — une phrase qui n'apprend rien à qui on demande de trancher.
    if (n.event_type === 'review_arbitration') {
      return t('pageNotifications.label.arbitration', {
        n: String(n.count ?? 0),
        scope: n.review_label || '—',
      });
    }
    if (n.event_type === 'action_created') {
      return t('pageNotifications.label.created', { text: txt });
    }
    if (n.event_type === 'action_deleted') {
      return t('pageNotifications.label.deleted', { text: txt });
    }
    const reasons = buildReasonsText(n);
    if (reasons) {
      return t('pageNotifications.label.updatedWithReasons', { text: txt, reasons });
    }
    return t('pageNotifications.label.updated', { text: txt });
  }

  function projectsLabel(n: Notification): string {
    const ids = Array.isArray(n.project_ids) ? n.project_ids : [];
    if (!ids.length) return '—';
    return ids
      .map((pid) => {
        const p = projectsById[pid];
        if (!p) return '#' + pid;
        return p.code ? `${p.code} · ${p.name}` : p.name;
      })
      .join(', ');
  }

  function fmtLocalDateTime(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
    return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  }

  function renderFiltersBar(): string {
    const programOpts = [
      '<option value="">' + esc(t('pageNotifications.filter.allPrograms')) + '</option>',
    ]
      .concat(
        (filterOpts.programs || [])
          .slice()
          .sort((a, b) => (a.code || '').localeCompare(b.code || ''))
          .map(
            (p) =>
              `<option value="${p.id}"${String(state.program_id) === String(p.id) ? ' selected' : ''}>${esc(p.code ? p.code + ' · ' + p.name : p.name)}</option>`,
          ),
      )
      .join('');
    const projectOpts = [
      '<option value="">' + esc(t('pageNotifications.filter.allProjects')) + '</option>',
    ]
      .concat(
        (filterOpts.projects || [])
          .slice()
          .sort((a, b) => (a.code || '').localeCompare(b.code || ''))
          .map(
            (p) =>
              `<option value="${p.id}"${String(state.project_id) === String(p.id) ? ' selected' : ''}>${esc(p.code ? p.code + ' · ' + p.name : p.name)}</option>`,
          ),
      )
      .join('');
    const structureOpts = [
      '<option value="">' + esc(t('pageNotifications.filter.allStructures')) + '</option>',
    ]
      .concat(
        structures
          .slice()
          .sort((a, b) => (a.code || '').localeCompare(b.code || ''))
          .map(
            (s) =>
              `<option value="${s.id}"${String(state.structure_id) === String(s.id) ? ' selected' : ''}>${esc(s.code + ' · ' + s.label)}</option>`,
          ),
      )
      .join('');
    const lvlOpts = [0, 1, 2, 3, 4]
      .map(
        (n) =>
          `<option value="${n}"${state.max_nn === n ? ' selected' : ''}>${esc(t('pageNotifications.filter.level', { n }))}</option>`,
      )
      .join('');
    const readOpts = `
      <option value=""${state.only_unread === '' ? ' selected' : ''}>${esc(t('pageNotifications.filter.readAll'))}</option>
      <option value="true"${state.only_unread === 'true' ? ' selected' : ''}>${esc(t('pageNotifications.filter.readUnread'))}</option>
      <option value="false"${state.only_unread === 'false' ? ' selected' : ''}>${esc(t('pageNotifications.filter.readRead'))}</option>
    `;
    return `
      <div class="card" style="padding:12px 16px;margin-bottom:12px;display:flex;flex-wrap:wrap;gap:12px;align-items:flex-end">
        <div style="flex:1;min-width:160px">
          <label style="font-size:11px;color:var(--text3);text-transform:uppercase;font-weight:600">${esc(t('pageNotifications.filter.program'))}</label>
          <select id="nf-program" style="width:100%">${programOpts}</select>
        </div>
        <div style="flex:1;min-width:160px">
          <label style="font-size:11px;color:var(--text3);text-transform:uppercase;font-weight:600">${esc(t('pageNotifications.filter.project'))}</label>
          <select id="nf-project" style="width:100%">${projectOpts}</select>
        </div>
        <div style="flex:1;min-width:160px">
          <label style="font-size:11px;color:var(--text3);text-transform:uppercase;font-weight:600">${esc(t('pageNotifications.filter.structure'))}</label>
          <select id="nf-structure" style="width:100%">${structureOpts}</select>
        </div>
        <div style="min-width:200px">
          <label style="font-size:11px;color:var(--text3);text-transform:uppercase;font-weight:600">${esc(t('pageNotifications.filter.maxNN'))}</label>
          <select id="nf-max-nn" style="width:100%">${lvlOpts}</select>
        </div>
        <div style="min-width:140px">
          <label style="font-size:11px;color:var(--text3);text-transform:uppercase;font-weight:600">${esc(t('pageNotifications.filter.read'))}</label>
          <select id="nf-read" style="width:100%">${readOpts}</select>
        </div>
      </div>
    `;
  }

  function renderRow(n: Notification): string {
    const dateStr = fmtLocalDateTime(n.created_at);
    const label = buildLabel(n);
    const projects = projectsLabel(n);
    const canOpen = n.action_alive && window.hasPermission && window.hasPermission('actions.read');
    const actionIdCol = n.action_id
      ? canOpen
        ? `<a href="#" data-open-action="${n.action_id}" style="color:var(--accent);text-decoration:underline;cursor:pointer">#${n.action_id}</a>`
        : `#${n.action_id}`
      : '—';
    const emitterChip =
      typeof window.trigramHtml === 'function'
        ? window.trigramHtml(n.emitter || {}, { placeholder: '—' })
        : esc((n.emitter && n.emitter.trigram) || '—');
    const emitterName = esc((n.emitter && n.emitter.name) || '');
    // Une sollicitation d'arbitrage ouvre la REVUE, pas une action : sans cette
    // branche elle afficherait « action supprimée », ce qui est faux et
    // décourage précisément la personne qu'on veut faire venir.
    const isArbitration = n.event_type === 'review_arbitration';
    const openBtn = isArbitration
      ? `<a class="btn btn-secondary btn-sm" href="#review/${n.review_id}">${esc(t('pageNotifications.btn.openReview'))}</a>`
      : canOpen
        ? `<button class="btn btn-secondary btn-sm" data-open-action="${n.action_id}">${esc(t('pageNotifications.btn.open'))}</button>`
        : n.action_alive
          ? `<span style="color:var(--text3);font-size:12px;font-style:italic">${esc(t('pageNotifications.actionNoPerm'))}</span>`
          : `<span style="color:var(--text3);font-size:12px;font-style:italic">${esc(t('pageNotifications.actionDeleted'))}</span>`;
    // Le message du sollicitant remplace la ligne des projets : c'est lui qui
    // porte le contexte (« comité du 12/09 »), pas un rattachement projet.
    const subLine = isArbitration
      ? n.message || t('pageNotifications.arbitrationNoMessage')
      : projects;
    const readBtnLabel = n.is_read
      ? t('pageNotifications.btn.markUnread')
      : t('pageNotifications.btn.markRead');
    const rowOpacity = n.is_read ? 'opacity:0.65;' : '';
    return `
      <div class="card notif-row" data-notif-id="${n.id}" style="padding:10px 14px;margin-bottom:6px;display:flex;gap:12px;align-items:center;${rowOpacity}">
        <div class="notif-date" style="flex-shrink:0;width:140px;font-size:12px;color:var(--text3)">${esc(dateStr)}</div>
        <div class="notif-action-id" style="flex-shrink:0;width:60px;font-size:12px;color:var(--text3);font-family:var(--font-mono,monospace)">${actionIdCol}</div>
        <div class="notif-emitter" style="flex-shrink:0;display:flex;align-items:center;gap:5px;width:160px;justify-content:flex-start">
          ${emitterChip}
          <span class="notif-emitter-name" style="font-size:12px;color:var(--text2);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${emitterName}</span>
        </div>
        <div class="notif-label" style="flex:1;min-width:0">
          <div style="font-size:13px;line-height:1.4">${label}</div>
          <div style="font-size:11px;color:var(--text3);margin-top:2px">${esc(subLine)}</div>
        </div>
        <div class="notif-btns" style="flex-shrink:0;display:flex;gap:6px;width:160px;justify-content:flex-end">
          ${openBtn}
          <button class="btn btn-secondary btn-sm" data-toggle-read="${n.id}">${esc(readBtnLabel)}</button>
        </div>
      </div>
    `;
  }

  function renderList(): string {
    if (!state.notifications.length) {
      return `<div class="empty-state" style="padding:32px;text-align:center;color:var(--text3)">${esc(t('pageNotifications.empty'))}</div>`;
    }
    return state.notifications.map(renderRow).join('');
  }

  function renderPage(): void {
    setContent(renderFiltersBar() + '<div id="notif-list">' + renderList() + '</div>');
    wireFilters();
    wireRows();
  }

  function wireFilters(): void {
    const refresh = (): void => {
      void (async () => {
        try {
          await loadNotifications();
        } catch (e: unknown) {
          const msg = e instanceof Error ? e.message : t('common.error');
          toast(msg, 'error');
          return;
        }
        const list = document.getElementById('notif-list');
        if (list) list.innerHTML = renderList();
        wireRows();
      })();
    };
    document.getElementById('nf-program')?.addEventListener('change', (e: Event) => {
      const target = e.target as HTMLSelectElement | null;
      state.program_id = target?.value || '';
      refresh();
    });
    document.getElementById('nf-project')?.addEventListener('change', (e: Event) => {
      const target = e.target as HTMLSelectElement | null;
      state.project_id = target?.value || '';
      refresh();
    });
    document.getElementById('nf-structure')?.addEventListener('change', (e: Event) => {
      const target = e.target as HTMLSelectElement | null;
      state.structure_id = target?.value || '';
      refresh();
    });
    document.getElementById('nf-max-nn')?.addEventListener('change', (e: Event) => {
      const target = e.target as HTMLSelectElement | null;
      const v = target?.value || '';
      state.max_nn = v === '' ? '' : parseInt(v, 10);
      refresh();
    });
    document.getElementById('nf-read')?.addEventListener('change', (e: Event) => {
      const target = e.target as HTMLSelectElement | null;
      state.only_unread = (target?.value || '') as '' | 'true' | 'false';
      refresh();
    });
  }

  function wireRows(): void {
    document.querySelectorAll<HTMLButtonElement>('[data-open-action]').forEach((btn) => {
      btn.addEventListener('click', (e: Event) => {
        if (btn.tagName === 'A') e.preventDefault();
        void api.withButton(btn, async () => {
          const aid = parseInt(btn.dataset['openAction'] || '0', 10);
          const sourceNotif =
            state.notifications.find((x) => x.action_id === aid && !x.is_read) ||
            state.notifications.find((x) => x.action_id === aid);
          let action: { id: number } | undefined;
          try {
            // GET /actions/{id} : on ne charge QUE l'action ouverte (et pas
            // toute la table) ; renvoie la même forme sérialisée que la liste.
            action = (await api.getAction(aid)) as { id: number };
          } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : t('common.error');
            toast(msg, 'error');
            return;
          }
          if (!action) {
            toast(t('pageNotifications.actionNotFound'), 'error');
            return;
          }
          let users: unknown;
          let statuses: unknown;
          let projects: unknown;
          try {
            [users, statuses, projects] = await Promise.all([
              api.getUsers(),
              api.getActionStatuses(),
              api.getProjects(),
            ]);
          } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : t('common.error');
            toast(msg, 'error');
            return;
          }
          if (sourceNotif && !sourceNotif.is_read) {
            try {
              await api.markNotifRead(sourceNotif.id);
              sourceNotif.is_read = true;
              if (typeof window.refreshNotifBadge === 'function') window.refreshNotifBadge();
            } catch {
              /* non bloquant */
            }
          }
          // Cast permissif sur les arguments — showEditActionModal vient de
          // component-action-modal/index.ts avec des types stricts. Ici on ne
          // les recompute pas (charge cognitive), cast au shape attendu.
          window.showEditActionModal(action as never, {
            users: users as never,
            statuses: statuses as never,
            projects: projects as never,
            onSaved: async () => {
              try {
                await loadNotifications();
              } catch {
                /* best-effort */
              }
              const list = document.getElementById('notif-list');
              if (list) list.innerHTML = renderList();
              wireRows();
              if (typeof window.refreshNotifBadge === 'function') window.refreshNotifBadge();
            },
          });
        });
      });
    });
    document.querySelectorAll<HTMLButtonElement>('[data-toggle-read]').forEach((btn) => {
      btn.addEventListener('click', () => {
        void api.withButton(btn, async () => {
          const id = parseInt(btn.dataset['toggleRead'] || '0', 10);
          const n = state.notifications.find((x) => x.id === id);
          if (!n) return;
          try {
            if (n.is_read) await api.markNotifUnread(id);
            else await api.markNotifRead(id);
            n.is_read = !n.is_read;
          } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : t('common.error');
            toast(msg, 'error');
            return;
          }
          const list = document.getElementById('notif-list');
          if (list) list.innerHTML = renderList();
          wireRows();
          if (typeof window.refreshNotifBadge === 'function') window.refreshNotifBadge();
        });
      });
    });
  }

  renderPage();
};

export {};
